import { type Graph, type GraphEdge, type GraphNode } from '../models/graph';
import { type Level } from '../models/level';
import { type MapTheme } from './theme';
import { bakeCityLayer } from './backgrounds';
import {
  bridgeDeck,
  type BridgeDeck,
  ensurePropsLoaded,
  fillNightPaper,
  propImage,
  PROPS,
} from './diorama';
import { currentLook } from './look';
import { type CityPlan, planCity } from './cityPlan';
import { ensureSpritesLoaded, onSpritesLoaded, spriteCatalog, spriteRevision } from './sprites';
import { boardCanal, levelLandmarks, levelRiver } from './geography';
import { planBoard } from './boardPlan';
import { ensureBoardArtLoaded } from './boardArt';
import {
  bakePaintedLayer,
  fitPaintedBoard,
  type PaintedBoard,
  paintedBoard,
  type PaintedView,
  requestPaintedBoard,
} from './paintedBoard';
import {
  fitBoard,
  fitViewport,
  insetKey,
  type Insets,
  type Viewport,
  uniformInsets,
} from './viewport';

export interface CanvasState {
  readonly selectedEdgeIds: ReadonlySet<string>;
  readonly hoveredEdgeId: string | null;
  readonly hintEdgeIds: ReadonlySet<string>;
  readonly edgeFlows: ReadonlyMap<string, number>;
  readonly cutMode: boolean;
  readonly showFlows: boolean;
  readonly routeEdgeIds?: ReadonlySet<string>;
  readonly routeHeadId?: string | null;
}

interface Point {
  x: number;
  y: number;
}

interface EdgeGeometry {
  readonly a: Point;
  readonly b: Point;
  readonly width: number;
  readonly labelAt: number;
  readonly decks: readonly BridgeDeck[];
}

const NODE_RADIUS = 15;
const HIT_TOLERANCE = 14;
const TOUCH_HIT_TOLERANCE = 22;
const GRAPH_MARGIN = NODE_RADIUS + 26;
const STREET_LIGHT_SPACING = 22;
const STREET_LIGHT = '#ffe6b0';
const STREET_LIGHT_RADIUS = 1.8;
const STREET_LIGHT_BLUR = 8;
const STREET_GLOW_BLUR = 18;
const SPRITE_CACHE_LIMIT = 48;
const ROAD_MARKING = 'rgba(232, 218, 186, 0.8)';
const ROAD_MARKING_LIT = '#fff1cf';
const LAMP_HEIGHT = 68;
const LANDMARK_HEIGHT = 92;
const SPRITE_SPACING = LANDMARK_HEIGHT * 1.3;
const MIN_SPRITE_SCALE = 0.25;
const ATMOSPHERE_FRAME_MS = 50;
const LAMP_FLAME = '#ffbf6e';
const FOG_PUFFS = 5;

function streetTheme(theme: MapTheme): MapTheme {
  return {
    ...theme,
    paper: '#efe4cc',
    paperEdge: '#b9a67e',
    inkSoft: '#e6d6b0',
    edge: '#b87333',
    edgeSelected: '#ffc766',
    edgeHover: '#f0a860',
    edgeCut: '#ff5a4a',
    node: '#1b2233',
    nodeStroke: '#c9a25a',
    source: '#3fd0c0',
    sink: '#ff5145',
  };
}

export class GraphCanvasRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private pixelRatio = 1;
  private insets: Insets = uniformInsets(GRAPH_MARGIN);
  private safe: Insets = uniformInsets(0);
  private currentGraph: Graph | null = null;
  private cityLayer: HTMLCanvasElement | null = null;
  private cityKey = '';
  private lastPaint: { level: Level; theme: MapTheme; state: CanvasState } | null = null;
  private readonly stopWatchingSprites: () => void;
  private readonly streets = currentLook() === 'diorama';
  private cityPlan: CityPlan | null = null;
  private painting: PaintedBoard | null = null;
  private paintingLevelId: string | null = null;
  private viewCache: {
    graph: Graph | null;
    width: number;
    height: number;
    insets: Insets;
    painting: PaintedBoard | null;
    view: Viewport;
    painted: PaintedView | null;
  } | null = null;
  private geometry: {
    graph: Graph;
    view: Viewport;
    plan: CityPlan | null;
    pixelRatio: number;
    edges: ReadonlyMap<string, EdgeGeometry>;
  } | null = null;
  private readonly sprites = new Map<string, HTMLCanvasElement>();
  private atmosphereFrame = 0;
  private atmosphereDrawn = 0;
  private clock = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.stopWatchingSprites =
      spriteRevision() > 0 ? () => {} : onSpritesLoaded(this.repaintWithSprites);
    void ensureSpritesLoaded();
    if (this.streets) {
      void Promise.all([ensurePropsLoaded(), ensureBoardArtLoaded()]).then(() => {
        this.cityKey = '';
        const last = this.lastPaint;
        if (last) this.render(last.level, last.theme, last.state);
      });
    }
  }

  dispose(): void {
    if (this.atmosphereFrame !== 0) cancelAnimationFrame(this.atmosphereFrame);
    this.atmosphereFrame = 0;
    this.stopWatchingSprites();
    this.cityLayer = null;
    this.lastPaint = null;
    this.geometry = null;
    this.sprites.clear();
  }

  private readonly repaintWithSprites = (): void => {
    this.stopWatchingSprites();
    this.cityKey = '';
    this.cityLayer = null;
    const last = this.lastPaint;
    if (last) this.render(last.level, last.theme, last.state);
  };

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.width = rect.width;
    this.height = rect.height;
    this.pixelRatio = dpr;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Keeps the graph out of the HUD; the margin around nodes is added on top. */
  setInsets(insets: Insets): void {
    this.safe = insets;
    this.insets = {
      top: insets.top + GRAPH_MARGIN,
      right: insets.right + GRAPH_MARGIN,
      bottom: insets.bottom + GRAPH_MARGIN,
      left: insets.left + GRAPH_MARGIN,
    };
  }

  private useLevel(level: Level): void {
    this.currentGraph = level.graph;
    if (!this.streets || level.id === this.paintingLevelId) return;
    this.paintingLevelId = level.id;
    this.painting = paintedBoard(level.id);
    if (this.painting) return;
    void requestPaintedBoard(level.id).then((board) => {
      if (!board || this.paintingLevelId !== level.id) return;
      this.painting = board;
      const last = this.lastPaint;
      if (last) this.render(last.level, last.theme, last.state);
    });
  }

  private get fitted(): { view: Viewport; painted: PaintedView | null } {
    const { currentGraph: graph, width, height, insets, painting } = this;
    const cached = this.viewCache;
    if (
      cached &&
      cached.graph === graph &&
      cached.width === width &&
      cached.height === height &&
      cached.insets === insets &&
      cached.painting === painting
    ) {
      return cached;
    }
    const painted = painting ? fitPaintedBoard(painting, width, height, this.safe) : null;
    const view =
      painted ??
      (this.streets
        ? fitBoard(graph, width, height, this.safe)
        : fitViewport(graph, width, height, insets));
    this.viewCache = { graph, width, height, insets, painting, view, painted };
    return this.viewCache;
  }

  private get view(): Viewport {
    return this.fitted.view;
  }

  private get painted(): PaintedView | null {
    return this.fitted.painted;
  }

  private toCanvas(position: { x: number; y: number }): Point {
    return this.view.project(position);
  }

  private edgeGeometry(graph: Graph): ReadonlyMap<string, EdgeGeometry> {
    this.currentGraph = graph;
    const view = this.view;
    const cached = this.geometry;
    if (
      cached &&
      cached.graph === graph &&
      cached.view === view &&
      cached.plan === this.cityPlan &&
      cached.pixelRatio === this.pixelRatio
    ) {
      return cached.edges;
    }

    const points = new Map(graph.nodes.map((node) => [node.id, view.project(node.position)]));
    const edges = new Map<string, EdgeGeometry>();
    for (const edge of graph.edges) {
      const a = points.get(edge.sourceId);
      const b = points.get(edge.targetId);
      if (!a || !b) continue;
      edges.set(edge.id, {
        a,
        b,
        width: this.streetWidth(edge.id),
        labelAt: this.labelPosition(edge.id, a, b),
        decks: this.streets ? this.decksOver(edge.id) : [],
      });
    }
    this.geometry = { graph, view, plan: this.cityPlan, pixelRatio: this.pixelRatio, edges };
    this.sprites.clear();
    return edges;
  }

  pointerPosition(event: PointerEvent | MouseEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  edgeAt(level: Level, point: Point, touch = false): GraphEdge | null {
    this.useLevel(level);
    const geometry = this.edgeGeometry(level.graph);
    let best: GraphEdge | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    const tolerance = touch ? TOUCH_HIT_TOLERANCE : HIT_TOLERANCE;

    for (const edge of level.graph.edges) {
      const g = geometry.get(edge.id);
      if (!g) continue;

      const reach = this.streets ? Math.max(tolerance, g.width / 2 + 4) : tolerance;
      const distance = distanceToSegment(point, g.a, g.b);
      if (distance < reach && distance < bestDistance) {
        bestDistance = distance;
        best = edge;
      }
    }
    return best;
  }

  nodeAt(level: Level, point: Point): GraphNode | null {
    this.useLevel(level);
    const reach = Math.max(NODE_RADIUS, this.painted?.plazaRadius ?? 0) + 4;
    for (const node of level.graph.nodes) {
      const p = this.toCanvas(node.position);
      const dx = p.x - point.x;
      const dy = p.y - point.y;
      if (Math.sqrt(dx * dx + dy * dy) <= reach) return node;
    }
    return null;
  }

  render(level: Level, theme: MapTheme, state: CanvasState): void {
    const { ctx } = this;
    this.useLevel(level);
    this.lastPaint = { level, theme, state };
    ctx.clearRect(0, 0, this.width, this.height);

    const cityLayer = this.cityLayerFor(level, theme);
    // The baked diorama layer is opaque, so paper under it would never show.
    if (!this.streets || !cityLayer) this.paintPaper(theme);
    if (cityLayer) ctx.drawImage(cityLayer, 0, 0, this.width, this.height);
    const painted = this.painted;
    if (painted) {
      this.clock = typeof performance === 'undefined' ? 0 : performance.now() / 1000;
      this.paintFog(painted);
      this.animateAtmosphere();
    }
    if (this.streets) {
      const lit = streetTheme(theme);
      this.paintStreetEdges(level, lit, state);
      this.paintLampNodes(level, lit, state);
    } else {
      this.paintEdges(level, theme, state);
      this.paintNodes(level, theme, state.routeHeadId ?? null);
    }
  }

  // Rendering is event-driven; only a painted board asks for frames, and only while shown.
  private animateAtmosphere(): void {
    if (this.atmosphereFrame !== 0 || typeof requestAnimationFrame !== 'function') return;
    if (
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    this.atmosphereFrame = requestAnimationFrame(this.tickAtmosphere);
  }

  private readonly tickAtmosphere = (now: number): void => {
    this.atmosphereFrame = 0;
    const last = this.lastPaint;
    if (!last || !this.painted || !this.canvas.isConnected) return;
    if (now - this.atmosphereDrawn < ATMOSPHERE_FRAME_MS) {
      this.animateAtmosphere();
      return;
    }
    this.atmosphereDrawn = now;
    this.render(last.level, last.theme, last.state);
  };

  private paintFog(painted: PaintedView): void {
    const { ctx, clock } = this;
    const puff = fogPuff();
    if (!puff) return;
    const { x, y, width: w, height: h } = painted.image;
    const size = h * 0.55;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (let i = 0; i < FOG_PUFFS; i++) {
      const drift = (i / FOG_PUFFS + clock * (0.006 + i * 0.0015)) % 1;
      const low = i % 2 === 0;
      const px = x - size / 2 + drift * (w + size);
      const py = low ? y + h - size * 0.3 : y + size * 0.05;
      ctx.globalAlpha = 0.16 + 0.06 * Math.sin(clock * 0.4 + i * 1.7);
      ctx.drawImage(puff, px - size / 2, py - size / 4, size, size / 2);
    }
    ctx.restore();
  }

  private flicker(index: number): number {
    const t = this.clock + index * 1.37;
    return 0.86 + 0.09 * Math.sin(t * 2.3) + 0.05 * Math.sin(t * 7.1 + index);
  }

  private paintPaper(theme: MapTheme): void {
    const { ctx } = this;
    if (this.streets) {
      fillNightPaper(ctx, 0, 0, this.width, this.height);
      return;
    }
    ctx.fillStyle = theme.paper;
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    ctx.strokeStyle = theme.paperEdge;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 1;
    const step = 32;
    ctx.beginPath();
    for (let x = step; x < this.width; x += step) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.height);
    }
    for (let y = step; y < this.height; y += step) {
      ctx.moveTo(0, y);
      ctx.lineTo(this.width, y);
    }
    ctx.stroke();
    ctx.restore();

    ctx.strokeStyle = theme.paperEdge;
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, this.width - 6, this.height - 6);
  }

  private cityPlanFor(level: Level): CityPlan {
    const catalog = spriteCatalog();
    if (this.streets) {
      const { board, frame, reach } = fitBoard(level.graph, this.width, this.height, this.safe);
      const canalPath = boardCanal(level);
      return planBoard(level, {
        board,
        frame,
        reach,
        catalog,
        landmarks: levelLandmarks(level, catalog),
        ...(canalPath === undefined ? {} : { canalPath }),
      });
    }
    return planCity(level, {
      scene: this.view.scene,
      catalog,
      canalPath: levelRiver(level),
      landmarks: levelLandmarks(level, catalog),
    });
  }

  private cityLayerFor(level: Level, theme: MapTheme): HTMLCanvasElement | null {
    if (!(this.view.scale > 0)) return null;

    const painted = this.painted;
    const key = `${painted ? 'painted' : 'board'}|${level.id}|${theme.id}|${this.width}x${this.height}@${this.pixelRatio}|${insetKey(this.insets)}|${spriteRevision()}`;
    if (key !== this.cityKey && painted && this.painting) {
      this.cityPlan = null;
      this.cityLayer = bakePaintedLayer(
        this.painting,
        painted,
        this.width,
        this.height,
        this.pixelRatio,
      );
      this.cityKey = key;
    } else if (key !== this.cityKey) {
      this.cityPlan = this.cityPlanFor(level);
      this.cityLayer = bakeCityLayer(
        this.cityPlan,
        theme,
        this.view.projection,
        this.width,
        this.height,
        this.pixelRatio,
      );
      this.cityKey = key;
    }
    return this.cityLayer;
  }

  private paintEdges(level: Level, theme: MapTheme, state: CanvasState): void {
    const { ctx } = this;
    const geometry = this.edgeGeometry(level.graph);

    for (const edge of level.graph.edges) {
      const g = geometry.get(edge.id);
      if (!g) continue;
      const { a, b } = g;

      const selected = state.selectedEdgeIds.has(edge.id);
      const hinted = state.hintEdgeIds.has(edge.id);
      const hovered = state.hoveredEdgeId === edge.id;
      const routed = state.routeEdgeIds?.has(edge.id) ?? false;
      const isOneWay = edge.metadata?.constraint === 'oneWay';

      ctx.save();

      if (routed) {
        ctx.strokeStyle = theme.edgeHover;
        ctx.lineWidth = 14;
        ctx.globalAlpha = 0.35;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      if (hinted && !selected) {
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = 10;
        ctx.globalAlpha = 0.28;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      if (selected && state.cutMode) {
        ctx.strokeStyle = theme.edgeCut;
        ctx.lineWidth = 4;
        ctx.setLineDash([7, 7]);
      } else if (state.showFlows) {
        const flow = state.edgeFlows.get(edge.id) ?? 0;
        const capacity = Math.max(1, Math.trunc(edge.capacity));
        ctx.strokeStyle = flow > 0 ? theme.edgeSelected : routed ? theme.edgeHover : theme.edge;
        ctx.lineWidth = flow > 0 ? 3 + 6 * Math.min(1, flow / capacity) : routed ? 4 : 3;
      } else if (selected) {
        ctx.strokeStyle = theme.edgeSelected;
        ctx.lineWidth = 6;
      } else if (hovered) {
        ctx.strokeStyle = theme.edgeHover;
        ctx.lineWidth = 5;
      } else {
        ctx.strokeStyle = theme.edge;
        ctx.lineWidth = 3;
      }

      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();

      if (selected && state.cutMode) this.paintCutMark(a, b, theme);
      if (isOneWay || level.graph.directed) this.paintArrow(a, b, theme, selected);

      this.paintEdgeLabel(level, edge, g, theme, state);
    }
  }

  private streetWidth(edgeId: string): number {
    const painted = this.painted;
    if (painted) return painted.streetWidth;
    const road = this.cityPlan?.roads.find((r) => r.edgeId === edgeId);
    return road ? Math.max(6, road.width * this.view.projection.unitScale) : 12;
  }

  private decksOver(edgeId: string): BridgeDeck[] {
    const plan = this.cityPlan;
    if (!plan) return [];
    return plan.crossings
      .filter((c) => c.under === edgeId)
      .map((c) => bridgeDeck(plan, c, this.view.projection))
      .filter((deck): deck is BridgeDeck => deck !== null);
  }

  private paintStreetEdges(level: Level, theme: MapTheme, state: CanvasState): void {
    const { ctx } = this;
    const geometry = this.edgeGeometry(level.graph);

    for (const edge of level.graph.edges) {
      const g = geometry.get(edge.id);
      if (!g) continue;
      const { a, b, width } = g;

      const selected = state.selectedEdgeIds.has(edge.id);
      const routed = state.routeEdgeIds?.has(edge.id) ?? false;

      let glow: { color: string; strength: number } | null = null;
      if (state.showFlows) {
        const flow = state.edgeFlows.get(edge.id) ?? 0;
        const capacity = Math.max(1, Math.trunc(edge.capacity));
        if (flow > 0)
          glow = {
            color: theme.edgeSelected,
            strength: 0.35 + 0.65 * Math.min(1, flow / capacity),
          };
        else if (routed) glow = { color: theme.edgeHover, strength: 0.45 };
      } else if (selected && !state.cutMode) {
        glow = { color: theme.edgeSelected, strength: 1 };
      } else if (routed) {
        glow = { color: theme.edgeHover, strength: 0.55 };
      } else if (state.hoveredEdgeId === edge.id) {
        glow = { color: theme.edgeHover, strength: 0.45 };
      } else if (state.hintEdgeIds.has(edge.id)) {
        glow = { color: theme.accent, strength: 0.4 };
      }

      if (glow) {
        ctx.save();
        if (g.decks.length > 0) {
          ctx.beginPath();
          ctx.rect(0, 0, this.width, this.height);
          for (const deck of g.decks) {
            deck.corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
            ctx.closePath();
          }
          ctx.clip('evenodd');
        }
        this.paintLitStreet(edge.id, a, b, width, glow.color, glow.strength);
        ctx.restore();
      }

      if (selected && state.cutMode) {
        ctx.save();
        ctx.strokeStyle = theme.edgeCut;
        ctx.lineWidth = 4;
        ctx.setLineDash([7, 7]);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.restore();
        this.paintCutMark(a, b, theme);
      }
      if (edge.metadata?.constraint === 'oneWay' || level.graph.directed) {
        this.paintRoadArrows(a, b, width, selected);
      }
      this.paintEdgeLabel(level, edge, g, theme, state);
    }
  }

  private paintRoadArrows(a: Point, b: Point, width: number, selected: boolean): void {
    const { ctx } = this;
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const size = Math.max(4, Math.min(8, width * 0.32));

    ctx.save();
    ctx.fillStyle = selected ? ROAD_MARKING_LIT : ROAD_MARKING;
    for (const t of [0.28, 0.72]) {
      const x = a.x + (b.x - a.x) * t;
      const y = a.y + (b.y - a.y) * t;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(angle) * size, y + Math.sin(angle) * size);
      ctx.lineTo(x + Math.cos(angle + 2.4) * size, y + Math.sin(angle + 2.4) * size);
      ctx.lineTo(x - Math.cos(angle) * size * 0.3, y - Math.sin(angle) * size * 0.3);
      ctx.lineTo(x + Math.cos(angle - 2.4) * size, y + Math.sin(angle - 2.4) * size);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  // Canvas shadows are blurred anew on every draw and made a single lit street
  // cost hundreds of milliseconds, so the halos are blurred once into sprites.
  private paintLitStreet(
    edgeId: string,
    a: Point,
    b: Point,
    width: number,
    color: string,
    strength: number,
  ): void {
    const { ctx, pixelRatio } = this;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length === 0) return;
    const nx = -(b.y - a.y) / length;
    const ny = (b.x - a.x) / length;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.5 * strength;
    const halo = this.cachedSprite(`halo|${edgeId}|${color}`, () =>
      streetHalo(length * pixelRatio, width * pixelRatio, color),
    );
    ctx.save();
    ctx.translate(a.x, a.y);
    ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x));
    ctx.scale(1 / pixelRatio, 1 / pixelRatio);
    ctx.drawImage(halo, -haloPad(), -halo.height / 2);
    ctx.restore();

    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();

    const alpha = Math.min(1, 0.4 + strength * 0.6);
    const light = this.cachedSprite(`light|${color}|${alpha}`, () =>
      streetLight(pixelRatio, color, alpha),
    );
    const size = light.width / pixelRatio;
    ctx.globalAlpha = 1;
    const offset = width / 2 + 1.5;
    const count = Math.max(1, Math.floor(length / STREET_LIGHT_SPACING));
    for (let i = 0; i <= count; i++) {
      const t = i / count;
      const x = a.x + (b.x - a.x) * t;
      const y = a.y + (b.y - a.y) * t;
      for (const side of [1, -1]) {
        const cx = x + nx * offset * side;
        const cy = y + ny * offset * side;
        ctx.drawImage(light, cx - size / 2, cy - size / 2, size, size);
      }
    }
    ctx.restore();
  }

  private cachedSprite(key: string, make: () => HTMLCanvasElement): HTMLCanvasElement {
    let sprite = this.sprites.get(key);
    if (!sprite) {
      if (this.sprites.size >= SPRITE_CACHE_LIMIT) this.sprites.clear();
      sprite = make();
      this.sprites.set(key, sprite);
    }
    return sprite;
  }

  private paintLampNodes(level: Level, theme: MapTheme, state: CanvasState): void {
    const { ctx } = this;
    const lit = new Set<string>();
    for (const edge of level.graph.edges) {
      if (!state.selectedEdgeIds.has(edge.id) && !(state.routeEdgeIds?.has(edge.id) ?? false)) {
        continue;
      }
      lit.add(edge.sourceId);
      lit.add(edge.targetId);
    }
    if (state.routeHeadId) lit.add(state.routeHeadId);

    const ordered = level.graph.nodes
      .map((node) => ({ node, p: this.toCanvas(node.position) }))
      .sort((x, y) => x.p.y - y.p.y);
    const board = this.cityPlan?.board;
    const painted = this.painted;
    const scale = painted
      ? painted.lampHeight / LAMP_HEIGHT
      : board
        ? (board.lampHeight * this.view.scale) / LAMP_HEIGHT
        : spriteScale(ordered.map((entry) => entry.p));
    const signs: { label: string; x: number; y: number }[] = [];

    for (const [index, { node, p }] of ordered.entries()) {
      const landmark =
        node.type === 'source' ? PROPS.source : node.type === 'sink' ? PROPS.sink : null;
      const glowColor =
        node.type === 'source'
          ? theme.source
          : node.type === 'sink'
            ? theme.sink
            : theme.edgeSelected;
      const on = landmark !== null || lit.has(node.id);
      const image = propImage(landmark ?? (on ? PROPS.lampLit : PROPS.lamp));
      if (!image) {
        this.paintNodes(
          { ...level, graph: { ...level.graph, nodes: [node] } },
          theme,
          state.routeHeadId ?? null,
        );
        continue;
      }

      if (on) this.paintGroundGlow(p, glowColor, (landmark ? 70 : 40) * scale);
      if (node.id === state.routeHeadId) {
        ctx.save();
        ctx.strokeStyle = theme.edgeHover;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, NODE_RADIUS + 8, (NODE_RADIUS + 8) * 0.55, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      const height = (landmark ? LANDMARK_HEIGHT : LAMP_HEIGHT) * scale;
      const width = height * (image.width / image.height);
      const foot = p.y + (landmark ? height * 0.18 : height * 0.1);
      ctx.drawImage(image, p.x - width / 2, foot - height, width, height);

      const flame = { x: p.x, y: foot - height * 0.8 };
      const flicker = painted ? this.flicker(index) : 1;
      if (!landmark && on) this.paintGroundGlow(flame, glowColor, 26 * scale * flicker);
      else if (!landmark && painted) {
        this.paintGroundGlow(flame, LAMP_FLAME, 20 * scale, 0.45 * flicker);
      }
      if (node.label) signs.push({ label: node.label, x: p.x, y: foot + 4 });
    }
    for (const sign of signs) this.paintNodeSign(sign.label, sign.x, sign.y, theme);
  }

  private paintGroundGlow(p: Point, color: string, radius: number, alpha = 0.75): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    glow.addColorStop(0, color);
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.globalAlpha = alpha;
    ctx.fillStyle = glow;
    ctx.fillRect(p.x - radius, p.y - radius, radius * 2, radius * 2);
    ctx.restore();
  }

  private paintNodeSign(label: string, x: number, y: number, theme: MapTheme): void {
    const { ctx } = this;
    ctx.save();
    ctx.font = '600 12px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const width = ctx.measureText(label).width;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = theme.paper;
    roundRect(ctx, x - width / 2 - 5, y - 2, width + 10, 17, 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = theme.ink;
    ctx.fillText(label, x, y + 0.5);
    ctx.restore();
  }

  private paintCutMark(a: Point, b: Point, theme: MapTheme): void {
    const { ctx } = this;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const angle = Math.atan2(b.y - a.y, b.x - a.x) + Math.PI / 2;
    const len = 11;

    ctx.save();
    ctx.strokeStyle = theme.edgeCut;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(mid.x - Math.cos(angle) * len, mid.y - Math.sin(angle) * len);
    ctx.lineTo(mid.x + Math.cos(angle) * len, mid.y + Math.sin(angle) * len);
    ctx.stroke();
    ctx.restore();
  }

  private paintArrow(a: Point, b: Point, theme: MapTheme, selected: boolean): void {
    const { ctx } = this;
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const tipDistance = NODE_RADIUS + 8;
    const tip = {
      x: b.x - Math.cos(angle) * tipDistance,
      y: b.y - Math.sin(angle) * tipDistance,
    };
    const size = 9;

    ctx.save();
    ctx.fillStyle = selected ? theme.edgeSelected : theme.inkSoft;
    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(
      tip.x - Math.cos(angle - Math.PI / 7) * size,
      tip.y - Math.sin(angle - Math.PI / 7) * size,
    );
    ctx.lineTo(
      tip.x - Math.cos(angle + Math.PI / 7) * size,
      tip.y - Math.sin(angle + Math.PI / 7) * size,
    );
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // A tag sitting on a crossing would hide the bridge and, with both edges
  // crossing at their middles, the other edge's tag as well.
  private labelPosition(edgeId: string, a: Point, b: Point): number {
    const crossings = this.streets
      ? (this.cityPlan?.crossings ?? []).filter((c) => c.over === edgeId || c.under === edgeId)
      : [];
    if (crossings.length === 0) return 0.5;
    const points = crossings.map((c) => this.view.project(c.at));
    const clearance = (t: number) => {
      const x = a.x + (b.x - a.x) * t;
      const y = a.y + (b.y - a.y) * t;
      return Math.min(...points.map((p) => Math.hypot(p.x - x, p.y - y)));
    };
    const offset = crossings.some((c) => c.over === edgeId) ? 0.02 : 0;
    return [0.5, 0.33 + offset, 0.67 - offset].reduce((best, t) =>
      clearance(t) > clearance(best) + 1 ? t : best,
    );
  }

  private paintEdgeLabel(
    level: Level,
    edge: GraphEdge,
    { a, b, labelAt: t }: EdgeGeometry,
    theme: MapTheme,
    state: CanvasState,
  ): void {
    const { ctx } = this;

    let label: string;
    if (state.showFlows) {
      label = `${state.edgeFlows.get(edge.id) ?? 0}/${Math.trunc(edge.capacity)}`;
    } else if (level.type === 'minCut' || level.type === 'maxFlow') {
      label = String(trimNumber(edge.capacity));
    } else {
      label = String(trimNumber(edge.weight));
    }

    const mid = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    ctx.save();
    ctx.font = '600 12px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const padding = 5;
    const textWidth = ctx.measureText(label).width;
    const boxWidth = textWidth + padding * 2;
    const boxHeight = 17;

    ctx.fillStyle = theme.paper;
    ctx.strokeStyle = theme.paperEdge;
    ctx.lineWidth = 1;
    roundRect(ctx, mid.x - boxWidth / 2, mid.y - boxHeight / 2, boxWidth, boxHeight, 4);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = theme.ink;
    ctx.fillText(label, mid.x, mid.y + 0.5);
    ctx.restore();
  }

  private paintNodes(level: Level, theme: MapTheme, routeHeadId: string | null): void {
    const { ctx } = this;

    for (const node of level.graph.nodes) {
      const p = this.toCanvas(node.position);
      const fill =
        node.type === 'source' ? theme.source : node.type === 'sink' ? theme.sink : theme.node;

      ctx.save();
      if (node.id === routeHeadId) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, NODE_RADIUS + 7, 0, Math.PI * 2);
        ctx.strokeStyle = theme.edgeHover;
        ctx.lineWidth = 4;
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(p.x, p.y, NODE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = theme.nodeStroke;
      ctx.stroke();

      if (node.type !== 'normal') {
        ctx.beginPath();
        ctx.arc(p.x, p.y, NODE_RADIUS - 5, 0, Math.PI * 2);
        ctx.strokeStyle = theme.paper;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      ctx.restore();

      if (node.label) {
        ctx.save();
        ctx.font = '600 12px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const y = p.y + NODE_RADIUS + 5;
        const width = ctx.measureText(node.label).width;

        ctx.fillStyle = theme.paper;
        ctx.globalAlpha = 0.85;
        roundRect(ctx, p.x - width / 2 - 4, y - 2, width + 8, 16, 3);
        ctx.fill();
        ctx.globalAlpha = 1;

        ctx.fillStyle = theme.ink;
        ctx.fillText(node.label, p.x, y);
        ctx.restore();
      }
    }
  }
}

// Fixed-size sprites would bury their neighbours and signs on a small screen.
function spriteScale(points: readonly Point[]): number {
  let spacing = Number.POSITIVE_INFINITY;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      spacing = Math.min(
        spacing,
        Math.hypot(points[i]!.x - points[j]!.x, points[i]!.y - points[j]!.y),
      );
    }
  }
  return Math.max(MIN_SPRITE_SCALE, Math.min(1, spacing / SPRITE_SPACING));
}

let puffSprite: HTMLCanvasElement | null | undefined;

function fogPuff(): HTMLCanvasElement | null {
  if (puffSprite !== undefined) return puffSprite;
  if (typeof document === 'undefined') return (puffSprite = null);
  const { canvas, ctx } = spriteCanvas(256, 128);
  const glow = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  glow.addColorStop(0, 'rgba(150, 165, 190, 0.55)');
  glow.addColorStop(0.5, 'rgba(120, 135, 160, 0.22)');
  glow.addColorStop(1, 'rgba(120, 135, 160, 0)');
  ctx.setTransform(1, 0, 0, 0.5, 0, 0);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 256, 256);
  return (puffSprite = canvas);
}

function haloPad(): number {
  return Math.ceil(STREET_GLOW_BLUR * 2.5);
}

// Offsets the shape off the canvas so only its blurred shadow lands on it.
function shadowOnly(ctx: CanvasRenderingContext2D, color: string, blur: number): number {
  const away = 4 * (ctx.canvas.width + ctx.canvas.height);
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetX = away;
  return away;
}

function spriteCanvas(
  width: number,
  height: number,
): {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width));
  canvas.height = Math.max(1, Math.ceil(height));
  return { canvas, ctx: canvas.getContext('2d')! };
}

/**
 * The shadow of a street stroke of `length` x `width` device pixels, lying
 * along x from haloPad(). A long street's blur is flat in the middle, so only
 * a short stretch is blurred and its middle column is stretched.
 */
function streetHalo(length: number, width: number, color: string): HTMLCanvasElement {
  const pad = haloPad();
  const run = Math.round(length);
  const height = Math.ceil(width + 2 * pad);
  const sample = Math.min(run, 2 * pad);
  const blurred = spriteCanvas(2 * pad + sample, height);
  const away = shadowOnly(blurred.ctx, color, STREET_GLOW_BLUR);
  blurred.ctx.fillRect(pad - away, (height - width) / 2, sample, width);
  if (run === sample) return blurred.canvas;

  const halo = spriteCanvas(2 * pad + run, height);
  const half = pad + sample / 2;
  halo.ctx.drawImage(blurred.canvas, 0, 0, half, height, 0, 0, half, height);
  halo.ctx.drawImage(blurred.canvas, half - 1, 0, 1, height, half, 0, run - sample, height);
  halo.ctx.drawImage(blurred.canvas, half, 0, half, height, half + run - sample, 0, half, height);
  return halo.canvas;
}

/** A curb light as painted with screen blending at `alpha`, centred in the sprite. */
function streetLight(pixelRatio: number, glow: string, alpha: number): HTMLCanvasElement {
  const radius = STREET_LIGHT_RADIUS * pixelRatio;
  const size = 2 * Math.ceil(radius + STREET_LIGHT_BLUR * 1.5 + 2);
  const { canvas, ctx } = spriteCanvas(size, size);
  ctx.globalCompositeOperation = 'screen';
  ctx.globalAlpha = alpha;
  ctx.fillStyle = STREET_LIGHT;
  ctx.shadowColor = glow;
  ctx.shadowBlur = STREET_LIGHT_BLUR;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
  ctx.fill();
  return canvas;
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y);

  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function trimNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
